import type { IsoWeekday, LunchMenuDay, MenuDish } from './dto/listing-menu'

export interface ViennaToday {
  /** YYYY-MM-DD as read off a clock in Vienna. */
  date: string
  weekday: IsoWeekday
}

const WEEKDAY_BY_NAME: Record<string, IsoWeekday> = {
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
  Sun: 7,
}

let viennaFormat: Intl.DateTimeFormat | null = null

function viennaCalendarDay(now: Date): ViennaToday | null {
  try {
    viennaFormat ??= new Intl.DateTimeFormat('en-US', {
      timeZone: 'Europe/Vienna',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      weekday: 'short',
    })
    const parts = Object.fromEntries(
      viennaFormat.formatToParts(now).map((part) => [part.type, part.value]),
    )
    const weekday = WEEKDAY_BY_NAME[parts.weekday ?? '']
    return weekday && parts.year && parts.month && parts.day
      ? { date: `${parts.year}-${parts.month}-${parts.day}`, weekday }
      : null
  } catch {
    return null
  }
}

/** Today's date and ISO weekday in Vienna; the device's own day where Intl has no time zones. */
export function viennaToday(now: Date): ViennaToday {
  const vienna = viennaCalendarDay(now)
  if (vienna) return vienna
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000)
  return {
    date: local.toISOString().slice(0, 10),
    weekday: (((now.getDay() + 6) % 7) + 1) as IsoWeekday,
  }
}

/** `date` (YYYY-MM-DD) moved by whole calendar days. */
export function addCalendarDays(date: string, days: number): string {
  const at = new Date(`${date}T12:00:00Z`)
  at.setUTCDate(at.getUTCDate() + days)
  return at.toISOString().slice(0, 10)
}

export function isMonday(date: string): boolean {
  const at = new Date(`${date}T12:00:00Z`)
  return !Number.isNaN(at.getTime()) && at.getUTCDay() === 1
}

/** The Monday of the week `today` falls in. */
export function mondayOf(today: ViennaToday): string {
  return addCalendarDays(today.date, 1 - today.weekday)
}

/** The ISO calendar week (the "KW" printed on Austrian boards) of `date` (YYYY-MM-DD). */
export function isoWeekNumber(date: string): number {
  const thursday = new Date(`${date}T12:00:00Z`)
  thursday.setUTCDate(thursday.getUTCDate() + 4 - (thursday.getUTCDay() || 7))
  const firstOfYear = Date.UTC(thursday.getUTCFullYear(), 0, 1, 12)
  return Math.floor((thursday.getTime() - firstOfYear) / (7 * 86_400_000)) + 1
}

export interface LunchMenuViewDay {
  weekday: IsoWeekday
  /** Null on a board that is the same every week. */
  date: string | null
  dishes: MenuDish[]
}

export interface LunchMenuView {
  days: LunchMenuViewDay[]
  initialWeekday: IsoWeekday
}

/**
 * What guests see of a Mittagsmenü today, or null when it shows nothing: the days with dishes
 * (for a one-week board only those from today on) and the day it opens on.
 */
export function lunchMenuView(
  menu: { weekOf: string | null; days: readonly LunchMenuDay[] },
  today: ViennaToday,
): LunchMenuView | null {
  const { weekOf } = menu
  const days = menu.days
    .filter((day) => day.dishes.length > 0)
    .sort((a, b) => a.weekday - b.weekday)
    .map((day) => ({
      weekday: day.weekday,
      date: weekOf ? addCalendarDays(weekOf, day.weekday - 1) : null,
      dishes: day.dishes,
    }))
  if (weekOf === null) {
    const opening = days.find((day) => day.weekday >= today.weekday) ?? days[0]
    return opening ? { days, initialWeekday: opening.weekday } : null
  }
  const upcoming = days.filter((day) => day.date !== null && day.date >= today.date)
  const opening = upcoming[0]
  return opening ? { days: upcoming, initialWeekday: opening.weekday } : null
}

export type LunchDayRelation = 'today' | 'tomorrow' | 'other'

export function lunchDayRelation(
  day: Pick<LunchMenuViewDay, 'weekday' | 'date'>,
  today: ViennaToday,
): LunchDayRelation {
  if (day.date !== null) {
    if (day.date === today.date) return 'today'
    return day.date === addCalendarDays(today.date, 1) ? 'tomorrow' : 'other'
  }
  if (day.weekday === today.weekday) return 'today'
  return day.weekday === (today.weekday % 7) + 1 ? 'tomorrow' : 'other'
}
